import {
  apiClient,
  clearStoredAuthState,
  getTokenStorageMode,
  setStoredCsrfToken,
  setStoredToken,
} from "./client";

function rememberFromStorageMode() {
  return getTokenStorageMode() !== "session";
}

export async function login(username, password, options = {}) {
  const { data } = await apiClient.post("/auth/login", { username, password });

  if (data?.authenticated) {
    setStoredToken("1", options);
    if (data?.csrfToken) {
      setStoredCsrfToken(data.csrfToken, options);
    }
  }

  return data;
}

export async function fetchAuthProviders() {
  const { data } = await apiClient.get("/auth/providers");
  return data;
}

export function getPortalLoginUrl() {
  const publicBase = String(process.env.PUBLIC_URL || "").replace(/\/$/, "");
  const apiBase = String(process.env.REACT_APP_API_BASE_URL || `${publicBase}/api`).replace(/\/$/, "");
  const returnUrl = `${publicBase}/` || "/";
  return `${apiBase}/auth/portal/login?returnUrl=${encodeURIComponent(returnUrl)}`;
}

export async function fetchApiHealth() {
  const { data } = await apiClient.get("/health");
  return data;
}

export async function requestForgotPasswordReset(username, email) {
  const { data } = await apiClient.post("/auth/forgot-password/request", {
    username,
    email,
  });
  return data;
}

export async function confirmForgotPasswordReset(
  username,
  email,
  resetCode,
  newPassword
) {
  const { data } = await apiClient.post("/auth/forgot-password/confirm", {
    username,
    email,
    resetCode,
    newPassword,
  });
  return data;
}

export async function fetchCurrentSession(options = {}) {
  const { data } = await apiClient.get("/auth/me");
  if (data?.csrfToken) {
    setStoredCsrfToken(data.csrfToken, {
      remember:
        options?.remember != null ? options.remember : rememberFromStorageMode(),
    });
  }
  return data;
}

export async function logout() {
  try {
    await apiClient.post("/auth/logout");
  } finally {
    clearStoredAuthState();
  }
}

export async function fetchProjects() {
  const { data } = await apiClient.get("/projects");
  return data;
}

export async function createProject(payload) {
  const { data } = await apiClient.post("/projects", payload);
  return data;
}

export async function renameProject(projectId, name, baseUpdatedAt = null) {
  const { data } = await apiClient.patch(`/projects/${projectId}`, {
    name,
    baseUpdatedAt,
  });
  return data;
}

export async function deleteProject(projectId, baseUpdatedAt = null) {
  await apiClient.delete(`/projects/${projectId}`, {
    params: baseUpdatedAt != null ? { baseUpdatedAt } : {},
  });
}

export async function fetchWorkspace(projectId, options = {}) {
  const { data } = await apiClient.get(`/projects/${projectId}/workspace`, {
    params:
      options.includePhotos === false
        ? { includePhotos: "false" }
        : undefined,
  });
  return data;
}

export async function saveMeasurementTemplate(
  projectId,
  measurementTemplate,
  baseUpdatedAt = null
) {
  const { data } = await apiClient.put(`/projects/${projectId}/template`, {
    measurementTemplate,
    baseUpdatedAt,
  });
  return data;
}

export async function fetchProjectPersonnel(projectId) {
  const { data } = await apiClient.get(`/projects/${projectId}/personnel`);
  return data;
}

export async function fetchProjectLogs(projectId, params = {}) {
  const { data } = await apiClient.get(`/projects/${projectId}/logs`, { params });
  return data;
}

export async function fetchProjectPackagingWorkspace(projectId) {
  const { data } = await apiClient.get(`/projects/${projectId}/packaging`);
  return data;
}

export async function createProjectPackagingRecord(projectId, payload) {
  const { data } = await apiClient.post(`/projects/${projectId}/packaging/records`, payload);
  return data;
}

export async function updateProjectPackagingRecord(projectId, recordId, payload) {
  const { data } = await apiClient.put(
    `/projects/${projectId}/packaging/records/${recordId}`,
    payload
  );
  return data;
}

export async function deleteProjectPackagingRecord(projectId, recordId, baseUpdatedAt = null) {
  const { data } = await apiClient.delete(
    `/projects/${projectId}/packaging/records/${recordId}`,
    {
      data: baseUpdatedAt == null ? {} : { baseUpdatedAt },
    }
  );
  return data;
}

export async function fetchProjectVehicles(projectId) {
  const { data } = await apiClient.get(`/projects/${projectId}/vehicles`);
  return data;
}

export async function fetchProjectVehicleKmRecords(projectId) {
  const { data } = await apiClient.get(`/projects/${projectId}/vehicle-km-records`);
  return data;
}

export async function saveProjectVehicleKmRecord(projectId, payload) {
  const { data } = await apiClient.put(`/projects/${projectId}/vehicle-km-records`, payload);
  return data;
}

export async function updateProjectVehicleKmRecord(projectId, recordId, payload) {
  const { data } = await apiClient.patch(
    `/projects/${projectId}/vehicle-km-records/${recordId}`,
    payload
  );
  return data;
}

export async function deleteProjectVehicleKmRecord(projectId, recordId) {
  await apiClient.delete(`/projects/${projectId}/vehicle-km-records/${recordId}`);
}

export async function fetchProjectVehicleFuelRecords(projectId) {
  const { data } = await apiClient.get(`/projects/${projectId}/vehicle-fuel-records`);
  return data;
}

export async function createProjectVehicleFuelRecord(projectId, payload) {
  const { data } = await apiClient.post(`/projects/${projectId}/vehicle-fuel-records`, payload);
  return data;
}

export async function updateProjectVehicleFuelRecord(projectId, recordId, payload) {
  const { data } = await apiClient.patch(
    `/projects/${projectId}/vehicle-fuel-records/${recordId}`,
    payload
  );
  return data;
}

export async function deleteProjectVehicleFuelRecord(projectId, recordId) {
  await apiClient.delete(`/projects/${projectId}/vehicle-fuel-records/${recordId}`);
}

export async function createProjectLog(projectId, payload) {
  const { data } = await apiClient.post(`/projects/${projectId}/logs`, payload);
  return data;
}

export async function createPafta(projectId, payload) {
  const { data } = await apiClient.post(`/projects/${projectId}/paftalar`, payload);
  return data;
}

export async function updatePafta(paftaId, payload) {
  const { data } = await apiClient.patch(`/paftalar/${paftaId}`, payload);
  return data;
}

export async function deletePafta(paftaId, baseUpdatedAt = null) {
  await apiClient.delete(`/paftalar/${paftaId}`, {
    params: baseUpdatedAt != null ? { baseUpdatedAt } : {},
  });
}

export async function createTeam(projectId, payload) {
  const { data } = await apiClient.post(`/projects/${projectId}/teams`, payload);
  return data;
}

export async function updateTeam(teamId, payload) {
  const { data } = await apiClient.patch(`/teams/${teamId}`, payload);
  return data;
}

export async function deleteTeam(teamId, baseUpdatedAt = null) {
  await apiClient.delete(`/teams/${teamId}`, {
    params: baseUpdatedAt != null ? { baseUpdatedAt } : {},
  });
}

export async function createPoint(projectId, payload) {
  const { data } = await apiClient.post(`/projects/${projectId}/points`, payload);
  return data;
}

export async function updatePoint(pointId, payload) {
  const { data } = await apiClient.patch(`/points/${pointId}`, payload);
  return data;
}

export async function importPafta(projectId, payload) {
  const { data } = await apiClient.post(`/projects/${projectId}/import/pafta`, payload);
  return data;
}

export async function fetchAdminPersonnel(projectId = null) {
  const { data } = await apiClient.get("/admin/personnel", {
    params: projectId ? { projectId } : {},
  });
  return data;
}

export async function createPersonnel(payload) {
  const { data } = await apiClient.post("/admin/personnel", payload);
  return data;
}

export async function updatePersonnel(personnelId, payload) {
  const { data } = await apiClient.patch(`/admin/personnel/${personnelId}`, payload);
  return data;
}

export async function assignPersonnel(personnelId, assignments, options = {}) {
  const { data } = await apiClient.put(`/admin/personnel/${personnelId}/assignments`, {
    assignments,
    ...options,
  });
  return data;
}

export async function removePersonnel(personnelId, projectId = null) {
  await apiClient.delete(`/admin/personnel/${personnelId}`, {
    params: projectId ? { projectId } : {},
  });
}

export async function fetchAdminLogs(params = {}) {
  const { data } = await apiClient.get("/admin/logs", { params });
  return data;
}

export async function fetchAdminAuditLogs(params = {}) {
  const { data } = await apiClient.get("/admin/audit-logs", { params });
  return data;
}

export async function fetchAdminProjectVehicles(projectId) {
  const { data } = await apiClient.get(`/admin/projects/${projectId}/vehicles`);
  return data;
}

export async function updateAdminProjectVehicleSettings(projectId, payload) {
  const { data } = await apiClient.put(`/admin/projects/${projectId}/vehicle-settings`, payload);
  return data;
}

export async function createAdminProjectVehicle(projectId, payload) {
  const { data } = await apiClient.post(`/admin/projects/${projectId}/vehicles`, payload);
  return data;
}

export async function updateAdminProjectVehicle(projectId, vehicleId, payload) {
  const { data } = await apiClient.patch(`/admin/projects/${projectId}/vehicles/${vehicleId}`, payload);
  return data;
}
